import * as React from 'react';
import { connect, Dispatch } from "react-redux";
import { IApplicationState, AppActionCreators, UserActionCreators, IAppState, IVerifyAuthCodeResponse } from "@nkr/store/index";
import { IBaseResponse, IUserResponse } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, HelpBlock, Panel } from 'react-bootstrap';
import MaskedInput from 'react-maskedinput';
import PasswordField from '@nkr/components/security/password-field';
import VerifyAuthCode from './verify-auth-code';

export enum UpdateType {
    None = 0,
    Phone = 1,
    Email = 2,
    Password = 3
}

enum UpdateStatus {
    None = 0,
    Successful = 1,
    Failed = 2,
    Sent = 3
}

interface IOwnProps {

}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getUser: (id: number) => Promise<IUserResponse>,
    updatePassword: (userId: number, currentPassword: string, newPassword: string, confirmNewPassword: string) => Promise<IBaseResponse>,
    sendAuthCode: (userId: number, isTwoFactor: boolean, mobileNumber: string, email: string) => Promise<IBaseResponse>,
    verifyAuthCode: (userId: number, code: string, isTwoFactor: boolean, mobileNumber: string, email: string) => Promise<IVerifyAuthCodeResponse>
}

type TProps =
    IOwnProps
    & IStateProps
    & IDispatchProps;

interface IValidation {
    email: boolean,
    mobileNumber: boolean,
    currentPassword: boolean,
    newPassword: boolean,
    confirmNewPassword: boolean,
}

interface IState {
    updateType: UpdateType,
    updateStatus: UpdateStatus,
    mobileNumber: string,
    newMobileNumber: string,
    email: string,
    newEmail: string,
    authCodeResults: {
        isValidCode: boolean,
        isValidTime: boolean,
    },
    expirePasswordDateTime: string,
    currentPassword: string,
    newPassword: string,
    confirmNewPassword: string,
    validation: IValidation,
    isDirty: boolean
}

class ManageAccount extends React.Component<TProps, IState> {
    readonly state: IState = {
        updateType: UpdateType.None,
        updateStatus: UpdateStatus.None,
        mobileNumber: '',
        newMobileNumber: '',
        email: '',
        newEmail: '',
        authCodeResults: {
            isValidCode: true,
            isValidTime: true,
        },
        expirePasswordDateTime: '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        validation: {
            email: true,
            mobileNumber: true,
            currentPassword: true,
            newPassword: true,
            confirmNewPassword: true,
        },
        isDirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting your information, one moment please...').then(() => {
            this.props.getUser(this.props.app.user.id).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    let { mobileNumber, email } = response.data.results.user;

                    this.setState({
                        mobileNumber,
                        email
                    });
                }
            });
        });
    }

    validate = (updateType: UpdateType) => {
        let { newMobileNumber, newEmail, currentPassword, newPassword, confirmNewPassword } = this.state;

        let validEmail = updateType == UpdateType.Email ? /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(newEmail) : true;
        let validMobileNumber = updateType == UpdateType.Phone ? newMobileNumber.replace(/_/g, '').length == 10 : true;
        let validCurrentPassword = updateType == UpdateType.Password ? currentPassword != '' : true;
        let validNewPassword = updateType == UpdateType.Password ? /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) && /\d/.test(newPassword) : true;
        let validConfirmNewPassword = updateType == UpdateType.Password ? newPassword == confirmNewPassword : true;

        let validation: IValidation = {
            email: validEmail,
            mobileNumber: validMobileNumber,
            currentPassword: validCurrentPassword,
            newPassword: validNewPassword,
            confirmNewPassword: validConfirmNewPassword
        };

        let isValid = validEmail && validMobileNumber && validCurrentPassword && validNewPassword && validConfirmNewPassword;

        return { validation, isValid };
    }

    handleInputChanged = (name: string, value: string) => {
        if (name == 'newMobileNumber') {
            this.setState({
                newMobileNumber: value
            });
        }

        if (name == 'newEmail') {
            this.setState({
                newEmail: value
            });
        }

        if (name == 'currentPassword') {
            this.setState({
                currentPassword: value
            });
        }

        if (name == 'newPassword') {
            this.setState({
                newPassword: value
            });
        }

        if (name == 'confirmNewPassword') {
            this.setState({
                confirmNewPassword: value
            });
        }
    }

    handleVerifyPhone = () => {
        let { validation, isValid } = this.validate(UpdateType.Phone);
        let userId = this.props.app.user.id;

        this.setState({
            updateType: UpdateType.Phone,
            validation,
            isDirty: true
        }, () => {
            if (isValid) {
                this.props.showLoading('Sending authorization code, one moment please...').then(() => {
                    this.props.sendAuthCode(userId, true, this.state.newMobileNumber, '').then(response => {
                        if (response.data.success) {
                            this.setState({
                                updateStatus: UpdateStatus.Sent
                            })
                        }

                        this.props.hideLoading();
                    });
                });
            }
        });
    }

    handleVerifyEmail = () => {
        let { validation, isValid } = this.validate(UpdateType.Email);
        let userId = this.props.app.user.id;

        this.setState({
            updateType: UpdateType.Email,
            validation,
            isDirty: true
        }, () => {
            if (isValid) {
                this.props.showLoading('Sending authorization code, one moment please...').then(() => {
                    this.props.sendAuthCode(userId, false, '', this.state.newEmail).then(response => {
                        if (response.data.success) {
                            this.setState({
                                updateStatus: UpdateStatus.Sent
                            })
                        }

                        this.props.hideLoading();
                    });
                });
            }
        });
    }

    handleUpdatePassword = () => {
        let { validation, isValid } = this.validate(UpdateType.Password);

        this.setState({
            updateType: UpdateType.Password,
            validation,
            isDirty: true
        }, () => {
            if (isValid) {
                let userId = this.props.app.user.id;
                let { currentPassword, newPassword, confirmNewPassword } = this.state;

                this.props.showLoading('Updating your password, one moment please...').then(() => {
                    this.props.updatePassword(userId, currentPassword, newPassword, confirmNewPassword).then(response => {
                        this.props.hideLoading();

                        this.setState({
                            updateStatus: response.data.success ? UpdateStatus.Successful : UpdateStatus.Failed
                        });
                    });
                });
            }
        });
    }

    handleCompleteVerification = (code: string) => {
        let { updateType, newEmail, newMobileNumber } = this.state;
        let isTwoFactor = updateType == UpdateType.Phone;
        let mobileNumber = updateType == UpdateType.Phone ? newMobileNumber : '';
        let email = updateType == UpdateType.Email ? newEmail : '';
        let userId = this.props.app.user.id;

        this.props.showLoading('Verifying code, one moment please...').then(() => {
            this.props.verifyAuthCode(userId, code, isTwoFactor, mobileNumber, email).then(response => {
                if (response.data.success) {
                    this.setState({
                        updateStatus: UpdateStatus.Successful,
                        mobileNumber: updateType == UpdateType.Phone ? newMobileNumber : this.state.mobileNumber,
                        newMobileNumber: '',
                        email: updateType == UpdateType.Email ? newEmail : this.state.email,
                        newEmail: ''
                    });
                } else {
                    let { isValidCode, isValidTime } = response.data.results;

                    this.setState({
                        updateStatus: UpdateStatus.Failed,
                        authCodeResults: {
                            isValidCode,
                            isValidTime
                        }
                    });
                }

                this.props.hideLoading();
            })
        });
    }

    handleCancelVerification = () => {
        this.setState({
            updateType: UpdateType.None,
            updateStatus: UpdateStatus.None
        });
    }

    render = () => {
        const { updateType, updateStatus, authCodeResults, validation, isDirty } = this.state;

        return (
            <div className='container'>
                <h3 style={{ marginBottom: 20 }}>Manage Account</h3>

                {updateStatus == UpdateStatus.Successful && (
                    <Alert bsStyle='success'>
                        {this.state.updateType == UpdateType.Password && (
                            <div>Your password was updated successfully.</div>
                        )}

                        {this.state.updateType == UpdateType.Email && (
                            <div>Your email was updated successfully.</div>
                        )}

                        {this.state.updateType == UpdateType.Phone && (
                            <div>Two-factor authentication has been enabled for your account.</div>
                        )}
                    </Alert>
                )}

                {(updateType == UpdateType.Email || updateType == UpdateType.Phone) && (updateStatus == UpdateStatus.Sent || updateStatus == UpdateStatus.Failed) && (
                    <VerifyAuthCode
                        updateType={updateType}
                        userName={this.props.app.user.username}
                        wasSent={updateStatus == UpdateStatus.Sent || updateStatus == UpdateStatus.Failed}
                        isValidCode={authCodeResults.isValidCode}
                        isValidTime={authCodeResults.isValidTime}
                        onComplete={this.handleCompleteVerification}
                        onCancel={this.handleCancelVerification}
                    />
                )}

                {updateStatus != UpdateStatus.Sent && updateStatus != UpdateStatus.Failed && (
                    <>
                        <Panel>
                            <Panel.Heading>
                                {this.state.mobileNumber != '' ?
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <h5 style={{ margin: 0 }}>Update Mobile Phone</h5>
                                        <div>Your current mobile # is set as: <strong>{this.state.mobileNumber}</strong></div>
                                    </div>
                                    :
                                    <h5 style={{ margin: 0 }}>Enable Two-Factor Authentication</h5>
                                }
                            </Panel.Heading>
                            <Panel.Body>
                                <form className='form-horizontal'>
                                    <FormGroup validationState={isDirty && !validation.mobileNumber ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>U.S. Mobile #:</Col>
                                        <Col xs={12} sm={4}>
                                            <div className='input-group'>
                                                <span className='input-group-addon'>1+</span>
                                                <MaskedInput
                                                    className="form-control"
                                                    id="newMobileNumber"
                                                    name="newMobileNumber"
                                                    placeholder="1235551212"
                                                    mask='1111111111'
                                                    maxLength={10}
                                                    value={this.state.newMobileNumber}
                                                    onChange={(e: React.FormEvent<HTMLInputElement>) => this.handleInputChanged(e.currentTarget.name, e.currentTarget.value)}
                                                />
                                            </div>
                                            {this.state.updateType == UpdateType.Phone && isDirty && !validation.mobileNumber && (
                                                <HelpBlock>Your mobile number is required</HelpBlock>
                                            )}
                                        </Col>
                                        <Col xs={12} sm={4}>
                                            <Button bsStyle='primary' onClick={this.handleVerifyPhone}>Verify Phone</Button>
                                        </Col>
                                    </FormGroup>
                                </form>
                            </Panel.Body>
                        </Panel>

                        <Panel>
                            <Panel.Heading><h5 style={{ margin: 0 }}>Update Email Address</h5></Panel.Heading>
                            <Panel.Body>
                                <Form horizontal>
                                    <FormGroup style={{ display: 'flex', alignItems: 'center' }}>
                                        <Col sm={4} componentClass={ControlLabel}>Current Email Address:</Col>
                                        <Col sm={4}>
                                            {this.state.email}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={isDirty && !validation.email ? 'error' : null}>
                                        <Col sm={4} componentClass={ControlLabel}>New Email Address:</Col>
                                        <Col sm={4}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="newEmail"
                                                name="newEmail"
                                                placeholder="e.g. me@example.com"
                                                maxLength={64}
                                                value={this.state.newEmail}
                                                onChange={(e: React.FormEvent<HTMLInputElement>) => this.handleInputChanged(e.currentTarget.name, e.currentTarget.value)}
                                            />
                                            {this.state.updateType == UpdateType.Email && isDirty && !validation.email && (
                                                <HelpBlock>Your new email address is required</HelpBlock>
                                            )}
                                        </Col>
                                        <Col sm={4}>
                                            <Button bsStyle='primary' onClick={this.handleVerifyEmail}>Verify Email</Button>
                                        </Col>
                                    </FormGroup>
                                </Form>
                            </Panel.Body>
                        </Panel>

                        <Panel>
                            <Panel.Heading><h5 style={{ margin: 0 }}>Update Password</h5></Panel.Heading>
                            <Panel.Body>
                                {this.state.expirePasswordDateTime != '' && (
                                    <Alert bsStyle='info'>Your password will expire on {this.state.expirePasswordDateTime}</Alert>
                                )}

                                <Form horizontal>
                                    <FormGroup validationState={isDirty && !validation.currentPassword ? 'error' : null}>
                                        <Col sm={4} componentClass={ControlLabel}>Current Password:</Col>
                                        <Col sm={4}>
                                            <PasswordField
                                                name='currentPassword'
                                                value={this.state.currentPassword}
                                                onChange={this.handleInputChanged} />
                                            {this.state.updateType == UpdateType.Password && isDirty && !validation.currentPassword && (
                                                <HelpBlock>Your current password is required</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={isDirty && !validation.newPassword ? 'error' : null}>
                                        <Col sm={4} componentClass={ControlLabel}>New Password:</Col>
                                        <Col sm={4}>
                                            <PasswordField
                                                name='newPassword'
                                                value={this.state.newPassword}
                                                onChange={this.handleInputChanged} />
                                            {this.state.updateType == UpdateType.Password && isDirty && !validation.newPassword && (
                                                <HelpBlock>Your new password is required and must contain at least 1 uppercase, 1 lowercase and 1 number</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={isDirty && !validation.confirmNewPassword ? 'error' : null}>
                                        <Col sm={4} componentClass={ControlLabel}>New Password Confirmation:</Col>
                                        <Col sm={4}>
                                            <PasswordField
                                                name='confirmNewPassword'
                                                value={this.state.confirmNewPassword}
                                                onChange={this.handleInputChanged} />
                                            {this.state.updateType == UpdateType.Password && isDirty && !validation.confirmNewPassword && (
                                                <HelpBlock>Your new password and new password confirmation must match</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup>
                                        <Col xs={12} sm={4} smOffset={4}>
                                            <Button bsStyle='primary' onClick={this.handleUpdatePassword}>Update Password</Button>
                                        </Col>
                                    </FormGroup>
                                </Form>
                            </Panel.Body>
                        </Panel>
                    </>
                )}

            </div>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return ({
        showLoading: (message): Promise<any> => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getUser: (id) => {
            return dispatch(UserActionCreators.getUser(id));
        },
        updatePassword: (userId, currentPassword, newPassword, confirmNewPassword) => {
            return dispatch(UserActionCreators.updatePassword(userId, currentPassword, newPassword, confirmNewPassword));
        },
        sendAuthCode: (userId, isTwoFactor, mobileNumber, email) => {
            return dispatch(UserActionCreators.sendAuthCode(userId, isTwoFactor, mobileNumber, email));
        },
        verifyAuthCode: (userId, code, isTwoFactor, mobileNumber, email) => {
            return dispatch(UserActionCreators.verifyAuthCode(userId, code, isTwoFactor, mobileNumber, email));
        }
    });
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(ManageAccount);